import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {Ticket, TicketData} from "types/Ticket";
import {useNotifications} from "context/NotificationContext";
import Button from "components/Button";
import {CheckCircleIcon, PencilIcon, TrashIcon, UserIcon, XCircleIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DeleteTicket from "pages/Ticket/Delete";
import {toTitleCase} from 'services/string';
import TicketStatus from "types/TicketStatus";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import {formattedDate} from "services/date";
import DescriptionListItem from "components/DescriptionListItem";
import DotBadge from "components/DotBadge";
import Chip from "components/Chip";
import TicketingSource from "types/TicketingSource";
import {invalidate} from "services/query-client";
import useMoney from "../../../hooks/useMoney";

// Map message status to badge color
const statusColors: Record<TicketStatus, number> = {
  [TicketStatus.UNPAID]: DotBadge.colors.YELLOW,
  [TicketStatus.PAID]: DotBadge.colors.BLUE,
  [TicketStatus.VALID]: DotBadge.colors.GREEN,
  [TicketStatus.EXPIRED]: DotBadge.colors.GRAY,
  [TicketStatus.REVOKED]: DotBadge.colors.RED,
}

function ViewTicket() {
  const {formattedMoney} = useMoney();
  const {notify} = useNotifications();
  const navigate = useNavigate();

  const {id} = useParams();
  const {data: ticket, isLoading} = useQuery(
    ['ticket', {id}],
    () => ngage.tickets.get(parseInt(id as string))
  )

  const update = useMutation((data: TicketData) => ngage.tickets.update(parseInt(id as string), data));

  const [deletingTicket, setDeletingTicket] = useState<Ticket | null>(null);

  // Prepare action handlers
  function handleDeleteTicket() {
    if (ticket) setDeletingTicket(ticket)
  }

  function handleEditTicket() {
    if (id) navigate(`/tickets/${id}/edit`)
  }

  async function handleRevokeTicket() {
    if (!ticket) return

    const revoking = !ticket.revoked_at;

    try {
      await update.mutateAsync({is_revoked: revoking})
      await invalidate(['ticket', {id}])

      if (revoking) {
        notify("success", "Ticket Revoked", `${ticket.number} has been revoked and can no longer be used`)
      } else {
        notify("success", "Ticket Reinstated", `${ticket.number} has been reinstated and can continue to be used`)
      }
    } catch (error) {
      notify("error", "Ticket Not Updated", `${ticket.number} could not be ${revoking ? 'revoked' : 'reinstated'}`)
    }
  }

  // Prepare page actions
  const actions = <>
    <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={handleDeleteTicket}
            disabled={!ticket || ticket.status === TicketStatus.VALID || ticket.source.name !== TicketingSource.NGAGE}>Delete</Button>
    <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={handleEditTicket}
            disabled={!ticket || ticket.source.name !== TicketingSource.NGAGE}>Edit</Button>
    <Button color={Button.colors.PRIMARY} icon={ticket?.status !== TicketStatus.REVOKED ? CheckCircleIcon : XCircleIcon}
            onClick={handleRevokeTicket} showSpinner={false}
            disabled={!ticket || ticket.source.name !== TicketingSource.NGAGE}>
      {ticket?.status !== TicketStatus.REVOKED ? 'Revoke' : 'Reinstate'}
    </Button>
  </>

  return (
    <Page
      title={ticket ? `Ticket ${ticket.number}` : "View Ticket"}
      breadcrumbs={{"Audience": "/", "Tickets": "/tickets", [ticket?.number || "View Ticket "]: `/tickets/${id}`}}
      actions={actions}
      isLoading={isLoading}
    >
      {!!ticket &&
        <div className="lg:grid lg:grid-cols-3 lg:gap-4">
          {/* Ticket Information */}
          <section className="lg:col-span-2">
            <Card
              heading="Ticket Information"
              subheading={toTitleCase(ticket.type.name)}
            >
              <DescriptionList>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Status</DescriptionTerm>
                  <DescriptionDetails>
                    <DotBadge color={statusColors[ticket.status]} text={ticket.status}/>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Issued</DescriptionTerm>
                  <DescriptionDetails>
                    {formattedDate(ticket.issued_at)}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Valid From</DescriptionTerm>
                  <DescriptionDetails>
                    {formattedDate(ticket.valid_at)}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Expires End</DescriptionTerm>
                  <DescriptionDetails>
                    {ticket.expires_at ? formattedDate(ticket.expires_at) : 'Never'}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={2}>
                  <DescriptionTerm>Type</DescriptionTerm>
                  <DescriptionDetails>
                    <Chip
                      name={ticket.type.name}
                      description={ticket.type.description || `Single-use ticket for ${formattedMoney(ticket.type.price, ticket.type.currency)}`}
                      href={`/ticket-types/${ticket.type.id}`}
                    />
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Price Paid</DescriptionTerm>
                  <DescriptionDetails>
                    {formattedMoney(ticket.price.discounted, ticket.currency)}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Discount Applied</DescriptionTerm>
                  <DescriptionDetails>
                    {!!ticket.discount.percent ? `${ticket.discount.percent}%` : 'None'}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={2}>
                  <DescriptionTerm>Holder</DescriptionTerm>
                  <DescriptionDetails>
                    <Chip
                      name={ticket.holder.name}
                      description="Ticket holder"
                      href={`/audience/${ticket.holder.id}`}
                    />
                  </DescriptionDetails>
                </DescriptionListItem>

                {ticket.source.name !== TicketingSource.NGAGE &&
                  <>
                    <DescriptionListItem span={1}>
                      <DescriptionTerm>Source</DescriptionTerm>
                      <DescriptionDetails>
                        {toTitleCase(ticket.source.name)}
                      </DescriptionDetails>
                    </DescriptionListItem>

                    <DescriptionListItem span={1}>
                      <DescriptionTerm>Reference</DescriptionTerm>
                      <DescriptionDetails>
                        {ticket.reference ?? 'N/A'}
                      </DescriptionDetails>
                    </DescriptionListItem>
                  </>
                }
              </DescriptionList>
            </Card>

            {/*<div className="mt-4">*/}
            {/*    <StatList>*/}
            {/*        <StatBox name="Visits" color={StatBox.colors.BLUE} icon={PlayIcon} stat={3} />*/}
            {/*        <StatBox name="Visit Cost" color={StatBox.colors.PURPLE} icon={CashIcon} stat={formattedMoney((ticket.price.discounted / 3), ticket.currency)} />*/}
            {/*        <StatBox name="Vouchers Used" color={StatBox.colors.PINK} icon={GiftIcon} stat={5} />*/}
            {/*    </StatList>*/}
            {/*</div>*/}
          </section>

          <section className="mt-4 lg:mt-0 lg:col-span-1">
            <Card heading="QR Code">
              <div className="relative flex justify-center items-center overflow-hidden">
                <img
                  src={ticket.bar_code.url}
                  alt={`${ticket.bar_code.type} for ticket ${ticket.number}`}
                  className="z-10"
                />
                <UserIcon className="absolute top-16 lg:top-12 left-0 right-0 h-auto text-gray-200"/>
              </div>
            </Card>
          </section>
        </div>
      }

      {/* Delete confirmation modal */}
      <DeleteTicket
        ticket={deletingTicket}
        show={ticket === deletingTicket}
        onCancel={() => setDeletingTicket(null)}
        onDelete={ticket => {
          navigate('/tickets')
          notify("success", "Ticket Deleted", `${ticket.number} has been permanently removed`)
        }}
        onDeleteFail={ticket => {
          setDeletingTicket(null)
          notify("error", "Ticket Not Deleted", `${ticket.number} could not be removed`)
        }}
      />
    </Page>
  );
}

export default ViewTicket;